import { AdgroupFormData, CampaignFormData, ProductSourceType, ProductSourceCanShow } from '@tt4b/ttam-enums';
import {
  useAdgroupIos14,
  useStdCampaignIs,
  useStdAdgroupIs,
  ValidationError,
  useGetProductSourceCanShowApi,
  useAllowlist,
} from '@tt4b/ttam-hooks';
import { computed, Ref, ref } from '@vue/composition-api';
import { i18n } from './../../../i18n';

import vsaShopDescImg from '@tt4b/ttam-assets/src/images/adgroup/ProductSource_TTShop.gif';
import vsaShowcaseDescImg from '@tt4b/ttam-assets/src/images/adgroup/ProductSource_Showcase.gif';
import vsaCatalogDescImg from '@tt4b/ttam-assets/src/images/adgroup/ProductSource_Catalog.gif';
import { createSharedComposable } from '@vueuse/core';

const isLoadingCanShow: Ref<boolean> = ref(false);
const canShowStatus: Ref<ProductSourceCanShow> = ref(null);
let getCanShowStatus: Promise<void>;

export const useProductSourceShow = createSharedComposable(function useProductSourceShow() {
  const loadProductSourceShow = () => {
    // 没有获取过则获取
    if (canShowStatus.value === null && !isLoadingCanShow.value) {
      isLoadingCanShow.value = true;

      getCanShowStatus = useGetProductSourceCanShowApi()
        .then(data => {
          isLoadingCanShow.value = false;
          canShowStatus.value = data;
        })
        .catch(() => {
          isLoadingCanShow.value = false;
        });
    }

    return getCanShowStatus;
  };

  const resetProductSourceShow = () => {
    canShowStatus.value = null;
    isLoadingCanShow.value = false;
  };

  const canShowShop = computed(() => Boolean(canShowStatus.value?.canShowShop));
  const canShowShowcase = computed(() => Boolean(canShowStatus.value?.canShowShowcase));

  return {
    loadProductSourceShow,
    resetProductSourceShow,
    isLoadingCanShow,
    canShowShop,
    canShowShowcase,
    canShowStatus,
  };
});

/**
 * 是否展示选择
 * ios 14 则隐藏来源选择，默认是From Catalog，只展示选择 Catalog
 * @deprecated 外层使用时已经在直接判断，组件内部的方法已经没用
 */
export function usePropsVisible(opt: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  isShopEmpty: Ref<boolean>;
}) {
  const { isIOS14Above } = useAdgroupIos14({
    campaignFormData: opt.campaignData,
    adgroupFormData: opt.adgroupData,
  });

  const { isCLA } = useStdAdgroupIs({
    campaignFormData: opt.campaignData,
    adgroupFormData: opt.adgroupData,
  });
  const visible = computed(() => !isIOS14Above.value);
  return {
    visible,
  };
}

/**
 * 获取展示的选项
 * @returns
 * 如果没有 shop 只展示 catalog，并默认选择 catalog，避免 confuse
 */
// eslint-disable-next-line max-lines-per-function
export function usePropsList(opt: {
  isCreate: Ref<boolean>;
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  // productSource: Ref<ProductSourceType>
}) {
  const { isCreate, adgroupData, campaignData } = opt;

  const { isIOS14Above } = useAdgroupIos14({ campaignFormData: campaignData, adgroupFormData: adgroupData });
  const { isProductSalesOpenLoop, isProductSalesCloseLoop } = useStdCampaignIs({ campaignFormData: campaignData });
  const { isCLA, isPSA } = useStdAdgroupIs({ campaignFormData: campaignData, adgroupFormData: adgroupData });
  const { isProductSalesCloseLoopWhite } = useAllowlist();

  const clickKey = 'module_ad_group_product_source_click';
  const showKey = 'module_ad_group_product_source_impression';

  const shop = {
    id: ProductSourceType.SHOP,
    title: i18n.t('module_product_source_shop'),
    desc: i18n.t('module_product_source_shop_vm_text'),
    ctaText: i18n.t('common_learn_more'),
    ctaLink: '/help/article/requesting-tiktok-shop-access-business-center',
    descImg: vsaShopDescImg,
    clickKey,
    showKey,
    canShow: true,
    trackProps: {
      'data-tea-click': clickKey,
      'data-tea-expose': showKey,
    },
    trackValue: {
      'data-tea-product_source': 'TikTok Shop',
    },
  };
  const catalog = {
    id: ProductSourceType.CATALOG,
    title: i18n.t('module_adgroup_dpa_catalogue_label'),
    desc: i18n.t('module_adgroup_dpa_catalogue_vm_text'),
    ctaText: i18n.t('common_learn_more'),
    ctaLink: '/help/article/catalogs?redirected=1',
    descImg: vsaCatalogDescImg,
    canShow: true,
    trackProps: {
      'data-tea-click': clickKey,
      'data-tea-expose': showKey,
    },
    trackValue: {
      'data-tea-product_source': 'Catalog',
    },
  };
  const showcase = {
    id: ProductSourceType.SHOWCASE,
    title: i18n.t('module_adgroup_vsa_showcase_label'),
    desc: i18n.t('module_adgroup_vsa_showcase_vm_text'),
    ctaText: i18n.t('module_adgroup_vsa_showcase_vm_cta'),
    ctaLink: '/help/article?aid=10013245&redirected=1',
    descImg: vsaShowcaseDescImg,
    canShow: true,
    trackProps: {
      'data-tea-click': clickKey,
      'data-tea-expose': showKey,
    },
    trackValue: {
      'data-tea-product_source': 'Showcase',
    },
  };

  // 折叠 product source
  function foldSource<T extends typeof catalog>(ps: T, notFold = false) {
    // canShow 为 false，就有被折叠起来
    return {
      ...ps,
      canShow: Boolean(notFold || adgroupData.value.productSource === ps.id),
    };
  }

  // 过滤没开白名单的
  function filterWhite<T extends typeof catalog>(psList: T[]) {
    return psList.filter(item => isProductSalesCloseLoopWhite.value || canShowStatus.value?.canShowShop);
  }

  const list = computed(() => {
    if (isIOS14Above.value || isCLA.value || isProductSalesOpenLoop.value) {
      return [catalog];
    }

    if (isProductSalesCloseLoop.value) {
      if (isPSA.value) {
        return [shop];
      }
      return [shop, showcase];
    }

    // TODO: 下面的是旧逻辑，可以删除
    const { canShowShop, canShowCatalog, canShowShowcase } = canShowStatus.value || {};

    if (isCreate.value) {
      // 显示逻辑 https://www.figma.com/file/wmWqFxPxzWmhf8be5M6CHV/Product-Sales?node-id=11491%3A43408
      // 有shop，优先显示shop
      if (canShowShop) {
        return [shop, ...filterWhite([foldSource(catalog, canShowCatalog), foldSource(showcase)])];
      }
      // 没有shop，有showcase，优先显示showcase
      if (canShowShowcase && !canShowShop) {
        return [catalog, showcase, ...filterWhite([foldSource(shop)])];
      }
      // 只有catalog，优先显示catalog
      if (canShowCatalog && !canShowShowcase && !canShowShop) {
        return [catalog, ...filterWhite([foldSource(shop), foldSource(showcase)])];
      }
    }

    return filterWhite([shop, catalog, showcase]);
  });

  return { list };
}

export function useRules() {
  return {
    productSourceTypeValidator: computed(() => [
      {
        trigger: 'change',
        validator: (rule, value, callback, formData: AdgroupFormData) => {
          if (!formData.productSource) {
            return callback(
              new ValidationError({
                messageI18nKey: 'adgroup_product_source_empty',
                message: i18n.t('adgroup_product_source_empty'),
                moduleName: i18n.t('adgroup_product_source'),
                subModuleName: i18n.t('adgroup_product_source'),
              }),
            );
          }
          return callback();
        },
      },
    ]),
  };
}
